import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './EncryptedDataViewer.css';

const EncryptedDataViewer = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDecrypted, setShowDecrypted] = useState({});
  const [encryptionKey, setEncryptionKey] = useState('');

  const API_BASE = 'http://localhost:5000/api';

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/devices`);
      setDevices(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load devices: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleDecryption = async (deviceId) => {
    if (!encryptionKey) {
      setError('Please enter the encryption key first');
      return;
    }

    setShowDecrypted(prev => ({
      ...prev,
      [deviceId]: !prev[deviceId]
    }));
  };

  const getEncryptedFields = (device) => {
    const encryptedFields = [];
    const fieldsToCheck = ['publicKey', 'serialNumber', 'hardwareId', 'macAddress'];
    
    fieldsToCheck.forEach(field => {
      if (device[field] && device[field] !== '') {
        encryptedFields.push({
          field,
          encryptedValue: device[field],
          isEncrypted: device[field].length > 20 // Simple check for encrypted data
        });
      }
    });
    
    return encryptedFields;
  };

  const clearMessages = () => {
    setError('');
  };

  return (
    <div className="encrypted-data-viewer">
      <div className="viewer-header">
        <h2>🔐 AES Encrypted Data Viewer</h2>
        <p>View and manage encrypted device data</p>
      </div>

      <div className="encryption-key-section">
        <div className="key-input-group">
          <label htmlFor="encryptionKey">Encryption Key:</label>
          <input
            type="password"
            id="encryptionKey"
            value={encryptionKey}
            onChange={(e) => setEncryptionKey(e.target.value)}
            placeholder="Enter AES encryption key"
            className="key-input"
          />
          <button 
            className="set-key-btn"
            onClick={() => setError('')}
          >
            Set Key
          </button>
        </div>
        <p className="key-info">
          💡 Enter the AES encryption key to decrypt sensitive device data
        </p>
      </div>

      {error && (
        <div className="error-message" onClick={clearMessages}>
          ❌ {error}
        </div>
      )}

      {loading ? (
        <div className="loading">🔄 Loading encrypted data...</div>
      ) : devices.length === 0 ? (
        <div className="no-data">No devices found with encrypted data</div>
      ) : (
        <div className="devices-grid">
          {devices.map(device => {
            const encryptedFields = getEncryptedFields(device);
            const hasEncryptedData = encryptedFields.length > 0;
            
            return (
              <div key={device.deviceId} className="device-card">
                <div className="device-header">
                  <h3>{device.deviceId}</h3>
                  <span className={`status-badge ${device.status}`}>
                    {device.status}
                  </span>
                </div>

                <div className="device-info">
                  <p><strong>Type:</strong> {device.type}</p>
                  <p><strong>Manufacturer:</strong> {device.manufacturer}</p>
                  <p><strong>Model:</strong> {device.model}</p>
                </div>

                {hasEncryptedData && (
                  <div className="encrypted-section">
                    <h4>🔒 Encrypted Fields ({encryptedFields.length})</h4>
                    
                    {encryptedFields.map(({ field, encryptedValue, isEncrypted }) => (
                      <div key={field} className="encrypted-field">
                        <div className="field-header">
                          <span className="field-name">{field}:</span>
                          <span className={`encryption-indicator ${isEncrypted ? 'encrypted' : 'plain'}`}>
                            {isEncrypted ? '🔐 Encrypted' : '📝 Plain Text'}
                          </span>
                        </div>
                        
                        <div className="field-value">
                          <div className="encrypted-value">
                            <strong>Encrypted:</strong>
                            <code>{encryptedValue}</code>
                          </div>
                          
                          {showDecrypted[device.deviceId] && encryptionKey && (
                            <div className="decrypted-value">
                              <strong>Decrypted:</strong>
                              <code>
                                Decryption requires proper AES service integration
                              </code>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    <button 
                      className="decrypt-btn"
                      onClick={() => toggleDecryption(device.deviceId)}
                      disabled={!encryptionKey}
                    >
                      {showDecrypted[device.deviceId] ? '🔒 Hide Decrypted' : '🔓 Show Decrypted'}
                    </button>
                  </div>
                )}

                {!hasEncryptedData && (
                  <div className="no-encrypted-data">
                    <p>📝 No encrypted fields found for this device</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="encryption-stats">
        <h3>📊 Encryption Statistics</h3>
        <div className="stats-grid">
          <div className="stat-item">
            <span className="stat-number">{devices.length}</span>
            <span className="stat-label">Total Devices</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {devices.filter(d => getEncryptedFields(d).length > 0).length}
            </span>
            <span className="stat-label">Devices with Encrypted Data</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {devices.reduce((total, d) => total + getEncryptedFields(d).length, 0)}
            </span>
            <span className="stat-label">Total Encrypted Fields</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EncryptedDataViewer; 